import _ from 'lodash'
import React, { FC, useState } from 'react'

import { useSticky } from '../../hooks/useSticky'
import { useViewportSize } from '../../hooks/useViewportSize'
import { useSessionStorage } from '../../hooks'
import { formatPath } from '../../utils/routes'

import { NavigationLink } from './NavMenu'
import PageHeader from './PageHeader'
import StickyPageHeader from './StickyPageHeader'
import MobilePageHeader from './Mobile/MobileHeader'
import MobileMenu from './Mobile/MobileMenu'
import { PageBannerProps } from './PageBanner'

export type Banner = {
  id: string
  title: string
  description: string
  link?: Link
  textColor: string
  image?: UmbracoImage
  backgroundColor?: string
  icon?: string
}

export type SearchFilter = {
  label: string
  identifier: string
  placeholder?: string
  countries?: CountryFilter[]
}

export type CountryFilter = {
  label: string
  countryCode: string
}

export type Link = {
  url: string
  text: string
  target?: string
}

export type UmbracoImage = {
  url: string
  alt?: string
}

export type ContactUsDetails = {
  name?: string
  email?: string
  phone?: string
  reason?: string
  message?: string
  url?: string
}

const mapLink = (l: { url: string; text: string; target?: string }) => {
  l.url = l.url ? formatPath(l.url) : ''
  l.text = l.text || ''
  return l
}

const createNavLinks = (navigationLinks: NavigationLink[]) => {
  return _.map(navigationLinks, l => {
    if (l.linkModel) {
      l.linkModel = mapLink(l.linkModel)
    }
    if (l.dropdownLinksMenuModel) {
      l.dropdownLinksMenuModel.links = _.map(l.dropdownLinksMenuModel.links, l => mapLink(l))
    }
    return l
  })
}

export type HeaderManagerProps = {
  countryFilterEnabled: boolean
  defaultCountryFilter?: {
    label: string
    countryCode: string
  }
  otherCountriesFilter?: {
    label: string
    countryCode: string
  }[]
  externalSearchLink?: { text: string; url: string }
  searchWidgetDisplayMode?: 'WithFilters' | 'WithoutFilters'
  cmsFaqUrl: string | undefined
  loggedIn: boolean
  userName: string
  fullName: string
  logoUrl: {
    url: string
    alt?: string
  }
  countOfOrderItems: number
  loginUrl: string
  showBulkImportMenuInHeader: boolean | undefined
  showBulkImportLinkAboveSearchButton: boolean | undefined
  showAsBulkImportButton: boolean | undefined
  showMulticolumnDropdown: boolean | undefined
  countUnreadNotifications: number
  selectedMenuItem: string | undefined
  logout: () => void
  loginAsLogout: () => void
  activeButton: 'Archive' | 'Profile' | 'BulkImport' | undefined
  links: {
    Archive: React.FunctionComponent
    Profile: React.FunctionComponent
    Bulk: React.FunctionComponent
    Cart: React.FunctionComponent
    Notifications: React.FunctionComponent
    Home: React.FunctionComponent
    Slash: React.FunctionComponent
    AdditionalBulkHistory: React.FunctionComponent
  }
  handleMarkAllNotificationsReadClick: () => void
  queryString: string
  onSearchClick: (params: { query: string; filter: string; countryFilter?: string }) => void
  changeLanguage: (lng: string) => void
  hasBriqBulkImportRole: boolean
  ImportHistoryLink: React.FunctionComponent
  toggle: (name: string) => void
  close: (name: string) => void
  navigationLinks: NavigationLink[]
  cmsHomeUrl: string | undefined
  searchFilters: SearchFilter[]
  footerLinks: {
    url: string
    text: string
    target?: string
  }[]
  logoImg: {
    url: string
    alt?: string
  }
  contactWidget:
    | {
        id: string
        title: string
        description: string
        link?: {
          url: string
          text: string
          target?: string
        }
        textColor: string
        image?: {
          url: string
          alt?: string
        }
        backgroundColor?: string
        icon?: string
      }
    | undefined
  showAiChatbotButton: boolean
  toggleAiChatbotWidget: () => void
} & PageBannerProps

export const HeaderManager: FC<HeaderManagerProps> = ({
  externalSearchLink,
  searchWidgetDisplayMode,
  countryFilterEnabled,
  defaultCountryFilter,
  otherCountriesFilter,
  cmsFaqUrl,
  loggedIn,
  userName,
  countOfOrderItems,
  fullName,
  loginUrl,
  logoUrl,
  showBulkImportMenuInHeader,
  countUnreadNotifications,
  showAsBulkImportButton,
  showBulkImportLinkAboveSearchButton,
  selectedMenuItem,
  logout,
  loginAsLogout,
  activeButton,
  links,
  handleMarkAllNotificationsReadClick,
  queryString,
  onSearchClick,
  changeLanguage,
  hasBriqBulkImportRole,
  ImportHistoryLink,
  toggle,
  close,
  navigationLinks,
  bannerData,
  cmsHomeUrl,
  searchFilters,
  footerLinks,
  logoImg,
  showMulticolumnDropdown,
  contactWidget,
  showAiChatbotButton,
  toggleAiChatbotWidget
}: HeaderManagerProps) => {
  const {
    Cart: CartLink,
    Slash: SlashLink,
    AdditionalBulkHistory: AdditionalBulkHistoryLink,
    Notifications: NotificationsLink
  } = links

  const navMenuItems = createNavLinks(navigationLinks)

  const { stickyElementRef, elementRef, isSticky } = useSticky()
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false)

  const viewPortSize = useViewportSize()

  const [closedBannerId, setClosedBannerId] = useSessionStorage('closedBanner', '')

  let header = null
  if (viewPortSize) {
    if (viewPortSize.isDesktop || viewPortSize.isWide) {
      header = (
        <>
          <div ref={elementRef}>
            <PageHeader
              bannerData={bannerData}
              homeUrl={formatPath(cmsHomeUrl)}
              navMenuItems={navMenuItems}
              searchOptions={searchFilters}
              searchWidgetDisplayMode={searchWidgetDisplayMode}
              externalSearchLink={externalSearchLink}
              closedBannerId={closedBannerId}
              setClosedBannerId={setClosedBannerId}
              countryFilterEnabled={countryFilterEnabled}
              defaultCountryFilter={defaultCountryFilter}
              otherCountriesFilter={otherCountriesFilter}
              cmsFaqUrl={formatPath(cmsFaqUrl)}
              countOfOrderItems={countOfOrderItems}
              countUnreadNotifications={countUnreadNotifications}
              fullName={fullName}
              loggedIn={loggedIn}
              loginUrl={loginUrl}
              logoUrl={logoUrl}
              selectedMenuItem={selectedMenuItem}
              showAsBulkImportButton={showAsBulkImportButton}
              showBulkImportLinkAboveSearchButton={showBulkImportLinkAboveSearchButton}
              showBulkImportMenuInHeader={showBulkImportMenuInHeader && hasBriqBulkImportRole}
              userName={userName}
              logout={logout}
              loginAsLogout={loginAsLogout}
              activeButton={activeButton}
              links={links}
              handleMarkAllNotificationsReadClick={handleMarkAllNotificationsReadClick}
              queryString={queryString}
              onSearchClick={onSearchClick}
              changeLanguage={changeLanguage}
              hasBriqBulkImportRole={hasBriqBulkImportRole}
              ImportHistoryLink={ImportHistoryLink}
              toggle={toggle}
              close={close}
              showMulticolumnDropdown={showMulticolumnDropdown}
              contactWidget={contactWidget}
              showAiChatbotButton={showAiChatbotButton}
              toggleAiChatbotWidget={toggleAiChatbotWidget}
            />
          </div>

          <StickyPageHeader
            bannerData={bannerData}
            homeUrl={formatPath(cmsHomeUrl)}
            navMenuItems={navMenuItems}
            searchWidgetDisplayMode={searchWidgetDisplayMode}
            searchOptions={searchFilters}
            visible={isSticky}
            ref={stickyElementRef}
            closedBannerId={closedBannerId}
            setClosedBannerId={setClosedBannerId}
            countryFilterEnabled={countryFilterEnabled}
            defaultCountryFilter={defaultCountryFilter}
            otherCountriesFilter={otherCountriesFilter}
            cmsFaqUrl={formatPath(cmsFaqUrl)}
            countOfOrderItems={countOfOrderItems}
            countUnreadNotifications={countUnreadNotifications}
            fullName={fullName}
            loggedIn={loggedIn}
            loginUrl={loginUrl}
            logoUrl={logoUrl}
            selectedMenuItem={selectedMenuItem}
            showAsBulkImportButton={showAsBulkImportButton}
            showBulkImportLinkAboveSearchButton={showBulkImportLinkAboveSearchButton}
            showBulkImportMenuInHeader={showBulkImportMenuInHeader && hasBriqBulkImportRole}
            userName={userName}
            activeButton={activeButton}
            handleMarkAllNotificationsReadClick={handleMarkAllNotificationsReadClick}
            onSearchClick={onSearchClick}
            hasBriqBulkImportRole={hasBriqBulkImportRole}
            toggle={toggle}
            close={close}
            links={links}
            queryString={queryString}
            ImportHistoryLink={ImportHistoryLink}
            logout={logout}
            loginAsLogout={loginAsLogout}
            changeLanguage={changeLanguage}
            showMulticolumnDropdown={showMulticolumnDropdown}
            contactWidget={contactWidget}
            showAiChatbotButton={showAiChatbotButton}
            toggleAiChatbotWidget={toggleAiChatbotWidget}
          />
        </>
      )
    } else {
      header = (
        <>
          <MobilePageHeader
            bannerData={bannerData}
            navMenuItems={navMenuItems}
            searchFilters={searchFilters}
            visible
            searchWidgetDisplayMode={searchWidgetDisplayMode}
            setMenuVisibility={setMobileMenuVisible}
            externalSearchLink={externalSearchLink}
            closedBannerId={closedBannerId}
            setClosedBannerId={setClosedBannerId}
            countryFilterEnabled={countryFilterEnabled}
            defaultCountryFilter={defaultCountryFilter}
            otherCountriesFilter={otherCountriesFilter}
            loginAsLogout={loginAsLogout}
            hasBriqBulkImportRole={hasBriqBulkImportRole}
            ImportHistoryLink={ImportHistoryLink}
            CartLink={CartLink}
            HomeLink={SlashLink}
            onSearchClick={onSearchClick}
            loggedIn={loggedIn}
            userName={userName}
            logoUrl={logoUrl}
            showBulkImportLinkAboveSearchButton={showBulkImportLinkAboveSearchButton}
            showAsBulkImportButton={showAsBulkImportButton}
            AdditionalImportHistoryLink={AdditionalBulkHistoryLink}
            countOfOrderItems={countOfOrderItems}
            loginUrl={loginUrl}
            queryString={queryString}
            showMulticolumnDropdown={showMulticolumnDropdown}
            NotificationsLink={NotificationsLink}
            handleMarkAllNotificationsReadClick={handleMarkAllNotificationsReadClick}
            countUnreadNotifications={countUnreadNotifications}
            isOpenedOverride={true}
            showAiChatbotButton={showAiChatbotButton}
            toggleAiChatbotWidget={toggleAiChatbotWidget}
          />
          <MobileMenu
            visible={mobileMenuVisible}
            setMenuVisibility={setMobileMenuVisible}
            showBulkImportMenuInHeader={hasBriqBulkImportRole && showBulkImportMenuInHeader}
            changeLanguage={changeLanguage}
            footerLinks={footerLinks}
            fullName={fullName}
            loggedIn={loggedIn}
            loginUrl={loginUrl}
            logoImg={logoImg}
            navigationLinks={navigationLinks}
            logout={logout}
            links={links}
          />
        </>
      )
    }
  }

  return header
}
